
import { useState } from 'react';
import { optimizeSupabaseImage, ImageOptimizationOptions } from '@/utils/imageOptimization';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  width: number;
  height: number;
  optimization?: ImageOptimizationOptions;
  fallbackSrc?: string;
}

export const OptimizedImage = ({ 
  src, 
  alt, 
  width, 
  height, 
  optimization,
  fallbackSrc,
  className,
  ...props 
}: OptimizedImageProps) => {
  const [imgError, setImgError] = useState(false);
  const [loading, setLoading] = useState(true);

  // Optimize the image URL if it's from Supabase
  const optimizedSrc = optimizeSupabaseImage(src, {
    width,
    height,
    ...optimization
  });

  const handleError = () => {
    setImgError(true);
    setLoading(false);
  };

  const handleLoad = () => {
    setLoading(false);
  };

  // Use fallback image if main image fails and fallback is provided
  const imageSrc = imgError && fallbackSrc ? fallbackSrc : optimizedSrc;

  return (
    <div className="relative" style={{ width, height }}>
      {loading && (
        <div 
          className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center"
          style={{ width, height }}
        >
          <span className="text-gray-400 text-sm">Loading...</span>
        </div>
      )}
      <img
        src={imageSrc}
        alt={alt}
        width={width}
        height={height}
        className={className}
        onError={handleError}
        onLoad={handleLoad}
        style={{
          display: loading ? 'none' : 'block',
          width,
          height,
          objectFit: 'cover'
        }}
        {...props}
      />
    </div>
  );
};
